/**
 * BoardMembersSection Component
 *
 * Återanvändbar komponent för att visa och hantera styrelseledamöter.
 * Används i både CompanyDetail och Step3Complete.
 */

import { useState } from "react";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { companiesApi, type BoardMember } from "@/lib/api";

// Roller för styrelseledamöter
const BOARD_ROLES = [
  { value: "Styrelseordförande", label: "Styrelseordförande" },
  { value: "Styrelseledamot", label: "Styrelseledamot" },
  { value: "Styrelsesuppleant", label: "Styrelsesuppleant" },
  { value: "VD", label: "VD" },
  { value: "Vice VD", label: "Vice VD" },
];

interface BoardMemberFormData {
  name: string;
  role: string;
  personalId: string;
  email: string;
}

interface BoardMembersSectionProps {
  companyId: string;
  members: BoardMember[];
  onMembersChange: (members: BoardMember[]) => void;
  editable?: boolean;
  showValidation?: boolean;
  compact?: boolean;
  /** Override save behavior for guest/local mode (bypasses API) */
  onSaveLocal?: (members: Array<{ name: string; role: string; personalId: string }>) => void;
}

export function BoardMembersSection({
  companyId,
  members,
  onMembersChange,
  editable = true,
  showValidation = false,
  compact = false,
  onSaveLocal,
}: BoardMembersSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingMembers, setEditingMembers] = useState<BoardMemberFormData[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation: Check if we have required roles
  const hasChairperson = members.some((m) => m.role === "Styrelseordförande");
  const hasOneMember = members.length >= 1;
  const isValid = hasChairperson && hasOneMember;

  const startEditing = () => {
    setEditingMembers(
      members.length > 0
        ? members.map((m) => ({
            name: m.name,
            role: m.role,
            personalId: m.personalId || "",
            email: m.email || "",
          }))
        : [{ name: "", role: "", personalId: "", email: "" }]
    );
    setIsEditing(true);
    setError(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingMembers([]);
    setError(null);
  };

  const addMember = () => {
    setEditingMembers([...editingMembers, { name: "", role: "", personalId: "", email: "" }]);
  };

  const removeMember = (index: number) => {
    setEditingMembers(editingMembers.filter((_, i) => i !== index));
  };

  const updateMember = (index: number, field: keyof BoardMemberFormData, value: string) => {
    const updated = [...editingMembers];
    updated[index] = { ...updated[index], [field]: value };
    setEditingMembers(updated);
  };

  const handleSave = async () => {
    // Validate input
    const validMembers = editingMembers.filter((m) => m.name.trim() && m.role);

    if (validMembers.length === 0) {
      setError("Lägg till minst en styrelseledamot");
      return;
    }

    // Validate personnummer - krävs för alla styrelseledamöter
    const missingPersonalId = validMembers.some((m) => !m.personalId?.trim() || m.personalId.trim().length < 10);
    if (missingPersonalId) {
      setError("Personnummer krävs för alla styrelseledamöter (minst 10 siffror)");
      return;
    }

    if (onSaveLocal) {
      // Guest/local mode: save without API call
      onSaveLocal(validMembers);
      // Build BoardMember-compatible objects with local IDs
      // personalId är redan validerat ovan, så det finns alltid
      const localMembers: BoardMember[] = validMembers.map((m, i) => ({
        id: `local-${Date.now()}-${i}`,
        companyId,
        name: m.name.trim(),
        role: m.role,
        personalId: m.personalId.trim(), // Validerat ovan
        email: m.email?.trim() || null,
        createdAt: new Date().toISOString(),
      }));
      onMembersChange(localMembers);
      setIsEditing(false);
      setEditingMembers([]);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await companiesApi.setBoardMembers(companyId, validMembers);

      if (response.success && response.data) {
        toast.success("Styrelseledamöter har sparats");
        onMembersChange(response.data.boardMembers);
        setIsEditing(false);
        setEditingMembers([]);
      } else {
        setError(response.error?.message || "Kunde inte spara styrelseledamöter");
      }
    } catch (err) {
      console.error("Save board members error:", err);
      toast.error("Kunde inte spara styrelseledamöter");
      setError("Ett fel uppstod vid sparande");
    } finally {
      setIsSaving(false);
    }
  };

  // Compact view (for wizard steps)
  if (compact) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h4 className="font-medium text-gray-900">Styrelse & VD</h4>
            {showValidation && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  isValid ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                }`}
              >
                {isValid ? "Komplett" : "Obligatoriskt"}
              </span>
            )}
          </div>
          {editable && !isEditing && (
            <button
              onClick={startEditing}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center"
            >
              {members.length > 0 ? (
                <>
                  <Edit size={14} className="mr-1" />
                  Redigera
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-1" />
                  Lägg till
                </>
              )}
            </button>
          )}
        </div>

        {isEditing ? (
          <EditingView
            editingMembers={editingMembers}
            onAdd={addMember}
            onRemove={removeMember}
            onUpdate={updateMember}
            onSave={handleSave}
            onCancel={cancelEditing}
            isSaving={isSaving}
            error={error}
            compact
          />
        ) : members.length > 0 ? (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="text-gray-500" size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 text-sm truncate">{member.name}</p>
                  <p className="text-xs text-gray-500">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-gray-50 rounded-lg">
            <Users className="mx-auto text-gray-300 mb-2" size={32} />
            <p className="text-sm text-gray-600">Inga styrelseledamöter registrerade</p>
            {showValidation && (
              <p className="text-xs text-amber-600 mt-1 flex items-center justify-center">
                <AlertCircle size={12} className="mr-1" />
                Krävs för årsredovisningen
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full view (for company detail page)
  return (
    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25 flex-shrink-0">
            <Users size={20} className="text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">Styrelse & VD</h2>
            <p className="text-xs text-gray-500">Hantera styrelseledamöter och VD</p>
          </div>
        </div>
        {editable && !isEditing && (
          <button
            onClick={startEditing}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-2.5 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center justify-center sm:justify-start gap-2 text-white font-medium text-sm sm:text-base">
              <Edit size={16} />
              <span>Redigera</span>
            </div>
          </button>
        )}
      </div>

      {isEditing ? (
        <EditingView
          editingMembers={editingMembers}
          onAdd={addMember}
          onRemove={removeMember}
          onUpdate={updateMember}
          onSave={handleSave}
          onCancel={cancelEditing}
          isSaving={isSaving}
          error={error}
        />
      ) : members.length > 0 ? (
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <Users className="text-gray-500" size={16} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{member.name}</p>
                  <p className="text-sm text-gray-500">{member.role}</p>
                </div>
              </div>
              {member.personalId && (
                <span className="text-xs text-gray-400">{member.personalId}</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <Users className="mx-auto text-gray-300 mb-3" size={40} />
          <p className="text-gray-600 mb-2">Inga styrelseledamöter registrerade</p>
          <p className="text-sm text-gray-500 mb-4">
            Lägg till styrelse och VD för att inkludera dem i årsredovisningen.
          </p>
          {editable && (
            <button
              onClick={startEditing}
              className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-2.5 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-center justify-center gap-2 text-white font-medium text-sm sm:text-base">
                <Plus size={16} />
                <span>Lägg till styrelseledamöter</span>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Editing view component
interface EditingViewProps {
  editingMembers: BoardMemberFormData[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, field: keyof BoardMemberFormData, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  error: string | null;
  compact?: boolean;
}

function EditingView({
  editingMembers,
  onAdd,
  onRemove,
  onUpdate,
  onSave,
  onCancel,
  isSaving,
  error,
  compact = false,
}: EditingViewProps) {
  return (
    <div className="space-y-4">
      {editingMembers.map((member, index) => (
        <div
          key={index}
          className={`p-4 border border-gray-200 rounded-lg ${compact ? "p-3" : ""}`}
        >
          <div className="flex items-start justify-between mb-3">
            <span className="text-xs text-gray-500">Person {index + 1}</span>
            {editingMembers.length > 1 && (
              <button
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          <div className={`grid gap-3 ${compact ? "grid-cols-1" : "md:grid-cols-2"}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Namn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={member.name}
                onChange={(e) => onUpdate(index, "name", e.target.value)}
                placeholder="T.ex. Anna Andersson"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Roll <span className="text-red-500">*</span>
              </label>
              <select
                value={member.role}
                onChange={(e) => onUpdate(index, "role", e.target.value)}
                className="input w-full"
              >
                <option value="">Välj roll...</option>
                {BOARD_ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={member.personalId}
                onChange={(e) => onUpdate(index, "personalId", e.target.value)}
                placeholder="YYYYMMDD-XXXX eller YYYYMMDDXXXX"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-postadress
              </label>
              <input
                type="email"
                value={member.email}
                onChange={(e) => onUpdate(index, "email", e.target.value)}
                placeholder="anna.andersson@example.com"
                className="input w-full"
              />
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center"
      >
        <Plus size={18} className="mr-2" />
        Lägg till person
      </button>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex space-x-3 pt-2">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="flex-1 btn btn-secondary"
        >
          <X size={16} className="mr-2" />
          Avbryt
        </button>
        <button
          onClick={onSave}
          disabled={isSaving}
          className="flex-1 btn btn-primary flex items-center justify-center"
        >
          {isSaving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Sparar...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Spara
            </>
          )}
        </button>
      </div>
    </div>
  );
}
